import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Calendar, Plus, X, CheckCircle, XCircle, Clock, VideoCamera,
  Star, NoteBlank, CaretDown
} from "@phosphor-icons/react";
import Pagination from "@/components/Pagination";

const STATUS_COLORS = {
  scheduled: "bg-blue-100 text-blue-700",
  confirmed: "bg-teal-100 text-teal-700",
  completed: "bg-green-100 text-green-700",
  cancelled: "bg-gray-100 text-gray-500",
  no_show: "bg-red-100 text-red-600",
};

const OUTCOME_COLORS = {
  passed: "bg-green-100 text-green-700",
  failed: "bg-red-100 text-red-600",
  on_hold: "bg-yellow-100 text-yellow-700",
  progressed: "bg-blue-100 text-blue-700",
};

const PLATFORM_ICONS = { zoom: "Zoom", teams: "MS Teams", meet: "Google Meet", in_person: "In Person" };

export default function InterviewsPage() {
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [showDetail, setShowDetail] = useState(null);
  const [showScorecard, setShowScorecard] = useState(false);
  const [scorecardForm, setScorecardForm] = useState({
    criteria: [
      { name: "Technical Skills", score: 0, notes: "" },
      { name: "Communication", score: 0, notes: "" },
      { name: "Cultural Fit", score: 0, notes: "" },
      { name: "Experience", score: 0, notes: "" },
      { name: "Problem Solving", score: 0, notes: "" },
    ],
    overall_rating: 0, recommendation: "", notes: "",
  });

  useEffect(() => { loadInterviews(); }, [filter, page]);

  const loadInterviews = async () => {
    try {
      const params = new URLSearchParams();
      if (filter) params.set("status", filter);
      params.set("page", page);
      params.set("limit", 25);
      const { data } = await api.get(`/interviews?${params}`);
      setInterviews(data.items || []);
      setTotal(data.total || 0);
    } catch { toast.error("Failed to load interviews"); }
    finally { setLoading(false); }
  };

  const handleAction = async (interviewId, action) => {
    try {
      if (action === "confirm") await api.post(`/interviews/${interviewId}/confirm`);
      else if (action === "complete_place") await api.post(`/interviews/${interviewId}/complete-and-place`);
      else await api.put(`/interviews/${interviewId}`, { status: action });
      toast.success("Updated");
      loadInterviews();
      if (showDetail?.id === interviewId) {
        const { data } = await api.get(`/interviews/${interviewId}`);
        setShowDetail(data);
      }
    } catch { toast.error("Failed"); }
  };

  const handleOutcome = async (interviewId, outcome) => {
    try {
      await api.put(`/interviews/${interviewId}`, { outcome });
      toast.success(`Outcome: ${outcome}`);
      loadInterviews();
      if (showDetail?.id === interviewId) {
        const { data } = await api.get(`/interviews/${interviewId}`);
        setShowDetail(data);
      }
    } catch { toast.error("Failed"); }
  };

  const handleSaveScorecard = async () => {
    if (!showDetail) return;
    try {
      await api.post(`/interviews/${showDetail.id}/scorecard`, scorecardForm);
      toast.success("Scorecard saved");
      setShowScorecard(false);
      const { data } = await api.get(`/interviews/${showDetail.id}`);
      setShowDetail(data);
    } catch { toast.error("Failed"); }
  };

  const updateCriteria = (idx, field, value) => {
    setScorecardForm(prev => {
      const criteria = [...prev.criteria];
      criteria[idx] = { ...criteria[idx], [field]: value };
      return { ...prev, criteria };
    });
  };

  const STATUSES = ["", "scheduled", "confirmed", "completed", "cancelled", "no_show"];

  return (
    <div data-testid="interviews-page" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-[#0F172A]">Interview Management</h1>
          <p className="text-sm text-[#6B7280] mt-1">Schedule, track, and manage all interviews</p>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        {STATUSES.map(s => (
          <button key={s || "all"} onClick={() => setFilter(s)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filter === s ? "bg-[#0F172A] text-white" : "bg-[#F3F4F6] text-[#6B7280] hover:bg-[#E5E7EB]"}`}
            data-testid={`filter-${s || "all"}`}>
            {s ? s.charAt(0).toUpperCase() + s.slice(1).replace("_", " ") : "All"}
          </button>
        ))}
      </div>

      {loading ? <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-20 skeleton-shimmer rounded-lg" />)}</div> :
        interviews.length === 0 ? (
          <div className="bg-white rounded-lg border p-12 text-center">
            <Calendar size={48} className="text-[#D1D5DB] mx-auto mb-3" />
            <p className="text-[#6B7280]">No interviews found</p>
          </div>
        ) : (
          <div className="space-y-2">
            {interviews.map(int => (
              <button key={int.id} onClick={() => setShowDetail(int)} className="w-full bg-white rounded-lg border border-[#E5E7EB] p-4 text-left hover:shadow-sm transition-shadow" data-testid={`interview-card-${int.id}`}>
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-[#0F172A]">{int.candidate_name || "Unknown"}</p>
                      <span className={`px-2 py-0.5 text-[10px] font-semibold uppercase rounded ${STATUS_COLORS[int.status] || "bg-gray-100"}`}>{int.status}</span>
                      {int.outcome && <span className={`px-2 py-0.5 text-[10px] font-semibold uppercase rounded ${OUTCOME_COLORS[int.outcome] || "bg-gray-100"}`}>{int.outcome}</span>}
                    </div>
                    <p className="text-xs text-[#6B7280] mt-0.5">
                      {int.interview_type} · {int.company_name || ""} · {int.scheduled_at ? new Date(int.scheduled_at).toLocaleString() : "TBD"}
                    </p>
                    {int.created_by_name && <p className="text-[10px] text-[#9CA3AF] mt-0.5">Created by {int.created_by_name}</p>}
                    <div className="flex gap-3 mt-1">
                      {int.meeting_platform && <span className="text-[10px] text-[#1E3A8A] flex items-center gap-0.5"><VideoCamera size={10} /> {PLATFORM_ICONS[int.meeting_platform] || int.meeting_platform}</span>}
                      {int.duration_minutes && <span className="text-[10px] text-[#6B7280] flex items-center gap-0.5"><Clock size={10} /> {int.duration_minutes}min</span>}
                      {int.scorecard?.overall_rating > 0 && <span className="text-[10px] text-[#C5A059] flex items-center gap-0.5"><Star size={10} weight="fill" /> {int.scorecard.overall_rating}/10</span>}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
        <Pagination page={page} total={total} limit={25} onPageChange={(p) => { setPage(p); setLoading(true); }} />

      {/* Detail Dialog */}
      {showDetail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 mx-4 max-h-[85vh] overflow-y-auto" data-testid="interview-detail-dialog">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="text-lg font-bold text-[#0F172A]">{showDetail.candidate_name}</h2>
                <p className="text-xs text-[#6B7280]">{showDetail.interview_type} interview · {showDetail.company_name}</p>
              </div>
              <button onClick={() => { setShowDetail(null); setShowScorecard(false); }}><X size={20} className="text-[#6B7280]" /></button>
            </div>

            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><span className="text-xs text-[#6B7280]">Date/Time</span><p className="font-medium">{showDetail.scheduled_at ? new Date(showDetail.scheduled_at).toLocaleString() : "TBD"}</p></div>
                <div><span className="text-xs text-[#6B7280]">Duration</span><p className="font-medium">{showDetail.duration_minutes} minutes</p></div>
                <div><span className="text-xs text-[#6B7280]">Status</span><p><span className={`px-2 py-0.5 text-[10px] font-semibold uppercase rounded ${STATUS_COLORS[showDetail.status]}`}>{showDetail.status}</span></p></div>
                <div><span className="text-xs text-[#6B7280]">Outcome</span><p>{showDetail.outcome ? <span className={`px-2 py-0.5 text-[10px] font-semibold uppercase rounded ${OUTCOME_COLORS[showDetail.outcome]}`}>{showDetail.outcome}</span> : <span className="text-[#6B7280]">Pending</span>}</p></div>
              </div>
              {showDetail.meeting_platform && <div><span className="text-xs text-[#6B7280]">Platform</span><p className="font-medium">{PLATFORM_ICONS[showDetail.meeting_platform]}</p></div>}
              {showDetail.meeting_link && <div><span className="text-xs text-[#6B7280]">Meeting Link</span><p className="text-[#1E3A8A] break-all">{showDetail.meeting_link}</p></div>}
              {showDetail.interviewer_name && <div><span className="text-xs text-[#6B7280]">Interviewer</span><p className="font-medium">{showDetail.interviewer_name}</p></div>}
              {showDetail.notes && <div><span className="text-xs text-[#6B7280]">Notes</span><p className="text-[#374151]">{showDetail.notes}</p></div>}

              {/* Actions */}
              <div className="flex flex-wrap gap-2 pt-2 border-t border-[#E5E7EB]">
                {showDetail.status === "scheduled" && <Button size="sm" variant="outline" onClick={() => handleAction(showDetail.id, "confirm")} className="text-xs h-7 gap-1"><CheckCircle size={12} /> Confirm</Button>}
                {["scheduled", "confirmed"].includes(showDetail.status) && (
                  <>
                    <Button size="sm" variant="outline" onClick={() => handleAction(showDetail.id, "completed")} className="text-xs h-7">Complete</Button>
                    <Button size="sm" variant="outline" onClick={() => handleAction(showDetail.id, "no_show")} className="text-xs h-7 text-orange-500">No Show</Button>
                    <Button size="sm" variant="outline" onClick={() => handleAction(showDetail.id, "cancelled")} className="text-xs h-7 text-red-500"><XCircle size={12} /> Cancel</Button>
                    <Button size="sm" onClick={() => handleAction(showDetail.id, "complete_place")} className="text-xs h-7 bg-[#10B981] gap-1"><CheckCircle size={12} /> Place</Button>
                  </>
                )}
                <Button size="sm" variant="outline" onClick={async () => {
                  if (!confirm("Delete this interview?")) return;
                  try {
                    await api.delete(`/interviews/${showDetail.id}`);
                    toast.success("Interview deleted");
                    setShowDetail(null);
                    loadInterviews();
                  } catch { toast.error("Delete failed"); }
                }} className="text-xs h-7 text-red-500 ml-auto" data-testid="delete-interview-btn"><XCircle size={12} /> Delete</Button>
              </div>
              {showDetail.created_by_name && <p className="text-[10px] text-[#9CA3AF]">Created by {showDetail.created_by_name}</p>}

              {/* Outcomes */}
              {showDetail.status === "completed" && !showDetail.outcome && (
                <div className="pt-2 border-t border-[#E5E7EB]">
                  <p className="text-xs text-[#6B7280] font-medium mb-2">Record Outcome</p>
                  <div className="flex gap-2">
                    {["passed", "failed", "on_hold", "progressed"].map(o => (
                      <Button key={o} size="sm" variant="outline" onClick={() => handleOutcome(showDetail.id, o)} className="text-xs h-7 capitalize">{o}</Button>
                    ))}
                  </div>
                </div>
              )}

              {/* Scorecard */}
              <div className="pt-2 border-t border-[#E5E7EB]">
                {showDetail.scorecard?.overall_rating > 0 ? (
                  <div>
                    <h4 className="text-sm font-semibold mb-2 flex items-center gap-1"><Star size={14} weight="fill" className="text-[#C5A059]" /> Scorecard</h4>
                    <div className="space-y-1">
                      {(showDetail.scorecard.criteria || []).map((c, i) => (
                        <div key={i} className="flex justify-between items-center py-1 text-xs"><span>{c.name}</span><span className="font-semibold">{c.score}/10</span></div>
                      ))}
                      <div className="flex justify-between items-center py-2 border-t font-semibold"><span>Overall</span><span className="text-[#C5A059]">{showDetail.scorecard.overall_rating}/10</span></div>
                      <p className="text-xs text-[#6B7280]">Recommendation: {showDetail.scorecard.recommendation}</p>
                    </div>
                  </div>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => setShowScorecard(!showScorecard)} className="text-xs gap-1" data-testid="open-scorecard-btn"><NoteBlank size={12} /> {showScorecard ? "Hide" : "Add"} Scorecard</Button>
                )}
                {showScorecard && (
                  <div className="mt-3 p-3 bg-[#F9FAFB] rounded-lg space-y-3" data-testid="scorecard-form">
                    {scorecardForm.criteria.map((c, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <span className="text-xs w-28 flex-shrink-0">{c.name}</span>
                        <Input type="number" min={0} max={10} value={c.score} onChange={e => updateCriteria(i, "score", parseInt(e.target.value) || 0)} className="w-16 h-7 text-xs" />
                        <Input placeholder="Notes" value={c.notes} onChange={e => updateCriteria(i, "notes", e.target.value)} className="flex-1 h-7 text-xs" />
                      </div>
                    ))}
                    <div className="flex items-center gap-2">
                      <span className="text-xs w-28 font-semibold">Overall /10</span>
                      <Input type="number" min={0} max={10} value={scorecardForm.overall_rating} onChange={e => setScorecardForm({...scorecardForm, overall_rating: parseInt(e.target.value) || 0})} className="w-16 h-7 text-xs" />
                    </div>
                    <select className="border rounded-lg px-2 py-1.5 text-xs w-full" value={scorecardForm.recommendation} onChange={e => setScorecardForm({...scorecardForm, recommendation: e.target.value})}>
                      <option value="">Recommendation</option>
                      <option value="hire">Hire</option>
                      <option value="no_hire">No Hire</option>
                      <option value="maybe">Maybe</option>
                    </select>
                    <textarea className="w-full border rounded-lg px-2 py-1.5 text-xs resize-none" rows={2} placeholder="Additional notes" value={scorecardForm.notes} onChange={e => setScorecardForm({...scorecardForm, notes: e.target.value})} />
                    <Button size="sm" onClick={handleSaveScorecard} className="bg-[#0F172A] text-xs" data-testid="save-scorecard-btn">Save Scorecard</Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
